import React, { useCallback, useMemo, useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import {
  Button,
  DialogActions,
  DialogContent,
  Popover,
  PopoverOrigin
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { CustomLabel } from 'modules/timesheet-labels/labelSchema';
import {
  LabelOrigin,
  LabelValueSource,
  SelectLabelOption
} from 'graphql/types';
import { CustomLabelKeyPicker } from './CustomLabelKeyPicker';
import { CustomLabelValuePicker } from './CustomLabelValuePicker';

const useStyles = makeStyles((theme) => ({
  action: {
    margin: theme.spacing(0, 2, 1)
  },
  add: {
    padding: theme.spacing(1, 5)
  }
}));

type CustomLabelSelectorPopperProps = {
  open: boolean;
  actionType: 'add' | 'update';
  anchorEl?: Element;
  handleClose: () => void;
  onChange: (customLabel: CustomLabel) => void;
  customLabel?: CustomLabel;
  fieldName: string;
};

const popoverOrigin: PopoverOrigin = {
  vertical: 'bottom',
  horizontal: 'left'
};
export const CustomLabelSelectorPopper: React.FC<CustomLabelSelectorPopperProps> = ({
  open,
  actionType,
  anchorEl,
  handleClose,
  onChange,
  customLabel,
  fieldName
}: CustomLabelSelectorPopperProps) => {
  const classes = useStyles();
  const customLabelValue = useMemo<SelectLabelOption | undefined>(() => {
    if (customLabel?.value) {
      return {
        id: customLabel.value.idValue!,
        name: customLabel.value.stringValue!
      };
    }
  }, [customLabel]);
  const [label, setLabel] = useState<CustomLabel | undefined>(customLabel);
  const [value, setValue] = useState<SelectLabelOption | undefined>(
    customLabelValue
  );

  const setCustomLabel = useCallback(
    (label: CustomLabel | undefined, resetValue = true) => {
      setLabel(label);
      resetValue && setValue(undefined);
    },
    []
  );

  const onSave = useCallback(() => {
    label &&
      onChange({
        id: label.id!,
        name: label.name!,
        origin: LabelOrigin.User,
        valueSource: LabelValueSource.User,
        type: label.type,
        value: { idValue: value?.id, stringValue: value?.name }
      });
    handleClose();
  }, [handleClose, label, onChange, value?.id, value?.name]);

  return (
    <Popover
      data-testid="custom-labels-popper"
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={popoverOrigin}
    >
      <DialogContent data-testid="custom-labels-popper-container">
        <CustomLabelKeyPicker
          value={label}
          setLabel={setCustomLabel}
          fieldName={fieldName}
        />
        <CustomLabelValuePicker
          setValue={setValue}
          label={label}
          value={value}
        />
      </DialogContent>
      <DialogActions
        className={classes.action}
        data-testid="custom-labels-popper-actions"
      >
        <Button
          onClick={handleClose}
          data-testid="cancel-custom-label-button"
          color="secondary"
        >
          <FormattedMessage id="customLabels.cancel" />
        </Button>
        <Button
          className={classes.add}
          onClick={onSave}
          data-testid="add-custom-label-button"
          color="primary"
          variant="contained"
          disabled={!label || !value}
        >
          <FormattedMessage
            id={
              actionType === 'add' ? 'customLabels.add' : 'customLabels.update'
            }
          />
        </Button>
      </DialogActions>
    </Popover>
  );
};
